import { Injectable } from '@angular/core';

import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { isComboSlipId } from '../../bet-generation/services/slip.utils';
import { ensureTabbedMode } from '../../linear/helpers';
import BetslipPickActions from '../../picks/actions';
import { betslipPicksListSelector } from '../../picks/selectors';
import { isPickEachWayCapable } from '../../picks/services/betslip-pick-helpers';
import { BetslipTypesActions } from '../../types/actions';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import BetslipErrorsActions from '../../validation/actions';
import BetslipComboBetActions from '../actions';
import { comboBetStateSelector } from '../selectors';

@Injectable()
export default class StakeEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private userSettings: SyncUserSettingsService,
    ) {}

    setComboBetStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.setStake),
            concatLatestFrom(() => this.store.select(betslipCurrentTypeSelector)),
            filter(([_, currentType]) => currentType === BetslipType.Combo),
            map(([action, _]) =>
                BetslipComboBetActions.setComboBetStake({
                    stake: action.stake,
                    silent: action.silent,
                    stakeStr: action.stakeStr,
                }),
            ),
        );
    });

    setDefaultStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.setSelectedType),
            ensureTabbedMode(this.store),
            concatLatestFrom(() => this.store.select(comboBetStateSelector)),
            filter(([action, state]) => action.betslipType === BetslipType.Combo && !state.stake),
            map(([_, _state]) =>
                BetslipComboBetActions.setComboBetStake({
                    stake: this.userSettings.defaultStake,
                    stakeStr: this.userSettings.defaultStake?.toString(),
                }),
            ),
        );
    });

    resetEachWay$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick, BetslipPickActions.removeMultiplePicks),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            filter(([_, pickList]) => !Object.values(pickList).some(isPickEachWayCapable)),
            map(() => BetslipComboBetActions.setComboEachWay({ isEachWay: false })),
        );
    });

    setComboBetStakeFromStakeHint$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.setStakeFromSlipStakeHint),
            filter((stakeHint) => isComboSlipId(stakeHint.slipStakeHint.slipId)),
            map(({ slipStakeHint }) => {
                return BetslipComboBetActions.setComboBetStake({ stake: slipStakeHint.newStakeHint, silent: true });
            }),
        );
    });
}
